import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './LeaderDashboard.module.css';
import PostForm from '../components/PostForm';

interface Post {
  id: number;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  postImageUrl?: string;
  videoUrl?: string;
  isVideoPost?: boolean;
}

const PostsManagement: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isPostEditModalOpen, setIsPostEditModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState<Post | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/posts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  };

  const deletePost = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await axios.delete(`/posts/${id}`);
      if (response.status === 200 || response.status === 204) {
        setPosts((prev) => prev.filter((p) => p.id !== id));
        alert('Post deleted successfully!');
      } else {
        console.warn(`Unexpected status code: ${response.status}`);
        fetchPosts();
      }
    } catch (err: any) {
      console.error('Failed to delete post:', err.response || err.message || err);
      alert('Post deletion failed. Check console logs for more info.');
    }
  };

  const handleEditPost = (post: Post) => {
    setPostToEdit(post);
    setIsPostEditModalOpen(true);
  };

  const renderEditPostModal = () => (
    <div className={styles.modalOverlay} onClick={() => setIsPostEditModalOpen(false)}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3>Edit Post</h3>
        <PostForm
          existingPost={postToEdit ?? undefined}
          onSuccess={() => {
            fetchPosts();
            setIsPostEditModalOpen(false);
            setPostToEdit(null);
          }}
        />
        <div className={styles.modalButtons}>
          <button
            className="button outline"
            onClick={() => setIsPostEditModalOpen(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.leaderDashboardContainer}>
      <div className={styles.card}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>Posts Management</h2>
          <button 
            className="button" 
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕ Cancel' : '➕ Create New Post'}
          </button>
        </div>

        {/* Create Post Form */}
        {showCreateForm && (
          <div className={styles.createPostSection}>
            <h3 className={styles.sectionTitle}>Create New Post</h3>
            <PostForm 
              onSuccess={() => {
                fetchPosts();
                setShowCreateForm(false);
              }} 
            />
          </div>
        )}

        {/* Posts List */}
        <div className={styles.postsListSection}>
          <h3 className={styles.sectionTitle}>All Posts ({posts.length})</h3>
          
          {posts.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No posts yet. Create your first post to get started!</p>
            </div>
          ) : (
            <div className={styles.postsGrid}>
              {posts.map((post) => (
                <div key={post.id} className={styles.postCard}>
                  {post.isPinned && (
                    <div className={styles.pinnedBadge}>
                      📌 Pinned
                    </div>
                  )}
                  
                  <h4 className={styles.postTitle}>{post.title}</h4>

                  {post.postImageUrl && (
                    <img src={post.postImageUrl} alt="Post" className={styles.postImage} />
                  )}

                  {post.isVideoPost && post.videoUrl && (
                    <div className={styles.videoBadge}>
                      🎥 Video Post
                    </div>
                  )}

                  <div className={styles.postMeta}>
                    <span>📅 {new Date(post.createdAt).toLocaleDateString()}</span>
                    <span>{new Date(post.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  <div className={styles.postActions}>
                    <button className="button small" onClick={() => handleEditPost(post)}>
                      ✏️ Edit
                    </button>
                    <button className="button outline small" onClick={() => deletePost(post.id)}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isPostEditModalOpen && renderEditPostModal()}
      </div>
    </div>
  );
};

export default PostsManagement;
